import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../DemoClient/Components/Card";
import { Input } from "../../DemoClient/Components/Input";
import { Search, Save, Building2, CreditCard, CheckCircle } from "lucide-react";
import { clientsData as initialClients, binData } from "../../DemoClient/Data/mockData";
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from "../../DemoClient/Components/Table";
import { TOAST } from "retro";
import { StatCard } from "../../DemoClient/Components/StatCard";
import { Navigation } from "retro";

export default function ClientMapping() {
  const [selectedCorporate, setSelectedCorporate] = useState(initialClients[0].id);
  const [searchQuery, setSearchQuery] = useState("");
  const [mappings, setMappings] = useState(
    initialClients.reduce((acc, corp) => ({ ...acc, [corp.id]: [...corp.mappedBINs] }), {})
  );

  const currentCorporate = initialClients.find(c => c.id === selectedCorporate);
  const currentMappings = mappings[selectedCorporate] || [];

  const filteredBINs = binData.filter(bin =>
    bin.bin.includes(searchQuery) ||
    bin.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bin.cardType.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleBINMapping = (binId) => {
    setMappings(prev => {
      const current = prev[selectedCorporate] || [];
      const updated = current.includes(binId)
        ? current.filter(id => id !== binId) // unmap
        : [...current, binId];              // map
      return { ...prev, [selectedCorporate]: updated };
    });
  };

  const handleSave = () => {
    console.log("Mappings for all clients:", mappings);
    TOAST.success("Mappings saved successfully!");
  };

  // Inline styles
  const gridStyle = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1rem" };
  const flexRow = { display: "flex", alignItems: "center", gap: "0.5rem" };
  const cardStyle = { borderRadius: "0.5rem", border: "1px solid #E5E7EB", padding: ".500rem", marginBottom: "1rem", backgroundColor: "#fff" };
  const inputStyle = { width: "100%", padding: "0.5rem", borderRadius: "0.375rem", border: "1px solid #D1D5DB", paddingLeft: "2rem" };
  const selectStyle = { width: "100%", padding: "0.5rem", borderRadius: "0.375rem", border: "1px solid #D1D5DB" };
  const badgeStyle = { padding: "0.25rem 0.5rem", borderRadius: "0.25rem", border: "1px solid #D1D5DB", display: "inline-block" };
  const thTdStyle = { border: "1px solid #E5E7EB", padding: "0.5rem", textAlign: "left" };

  return (
    <div>
       <Navigation
        backUrl={""}
        title="Client Mapping"
        chips={["Client Mapping"]}
        description="Client mapping is the process of mapping a client to a BIN."
      />
      <div style={gridStyle} className="mt2">

        <StatCard
              title="Total Clients"
              value={initialClients.length}
              icon={<CheckCircle style={{ width: "1.25rem", height: "1.25rem" }} />}
              iconColor="#DBEAFE"
            />
   <StatCard
              title="Total Mapped BINs"
              value={currentMappings.length}
              icon={<CheckCircle style={{ width: "1.25rem", height: "1.25rem" }} />}
              iconColor="#DBEAFE"
            />
  <StatCard
              title="Total Available BINs"
              value={binData.length}
              icon={<CreditCard style={{ width: "1.25rem", height: "1.25rem" }} />}
              iconColor="#DBEAFE"
            />
      </div>

      {/* Corporate Selection and Search */}
      <Card style={cardStyle}>
        <CardContent>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <div style={{ flex: 1,marginTop:"15px" }}>
              <label style={{ fontSize: "0.875rem", fontWeight: 500, display: "block", marginBottom: "0.25rem" }}>Select Clients</label>
              <select
                style={selectStyle}
                value={selectedCorporate}
                onChange={(e) => setSelectedCorporate(e.target.value)}
              >
                {initialClients.map(corp => (
                  <option key={corp.id} value={corp.id}>{corp.name}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, position: "relative",marginTop:"15px" }}>
              <label style={{ fontSize: "0.875rem", fontWeight: 500, display: "block", marginBottom: "0.25rem" }}>Search BINs</label>
              <Search style={{ position: "absolute", left: "0.5rem", top: "70%", transform: "translateY(-50%)", color: "#6B7280" }} />
              <Input
                style={{ ...inputStyle, paddingLeft: "3rem" }}
                placeholder="Search by BIN, brand, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                style={{ padding: "0.5rem 1rem", backgroundColor: "#2563EB", color: "#fff", borderRadius: "0.375rem", display: "flex", alignItems: "center", gap: "0.5rem", border: "none", cursor: "pointer" }}
                onClick={handleSave}
              >
                <Save style={{ width: "1rem", height: "1rem" }} /> Save Mappings
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BIN Table */}
      <Card style={cardStyle}>
        <CardHeader>
          <CardTitle>BIN Mapping for {currentCorporate?.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table style={{ width: "100%", borderCollapse: "collapse" }} maxHeight="350px">
            <TableHeader>
              <TableRow>
                <TableHead style={thTdStyle}>Mapped</TableHead>
                <TableHead style={thTdStyle}>BIN</TableHead>
                <TableHead style={thTdStyle}>Brand</TableHead>
                <TableHead style={thTdStyle}>Card Type</TableHead>
                <TableHead style={thTdStyle}>Card Nature</TableHead>
                <TableHead style={thTdStyle}>Funding Type</TableHead>
                <TableHead style={thTdStyle}>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBINs.map(bin => (
                <TableRow key={bin.id}>
                  <TableCell style={thTdStyle}>
                    <input
                      type="checkbox"
                      checked={currentMappings.includes(bin.bin)}
                      onChange={() => toggleBINMapping(bin.bin)}
                    />
                  </TableCell>
                  <TableCell style={{ ...thTdStyle, fontFamily: "monospace", fontWeight: 500 }}>{bin.bin}</TableCell>
                  <TableCell style={thTdStyle}>
                    <span style={badgeStyle}>{bin.brand}</span>
                  </TableCell>
                  <TableCell style={thTdStyle}>{bin.cardType}</TableCell>
                  <TableCell style={thTdStyle}>{bin.cardNature}</TableCell>
                  <TableCell style={thTdStyle}>{bin.fundingType}</TableCell>
                  <TableCell style={thTdStyle}>
                    <span style={{ ...badgeStyle, borderColor: bin.status === "Active" ? "green" : "red", color: bin.status === "Active" ? "green" : "red" }}>
                      {bin.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
